import * as React from 'react'
import Box from '@mui/material/Box'
import VendorContactHeader from 'vendors/components/vendorContact/VendorContactHeader'
import Button from '@mui/material/Button'
import AddVendorContactDialog from 'vendors/components/vendorContact/dialogs/AddVendorContactDialog'
import { useParams, Navigate } from 'react-router-dom'
import VendorContactCards from 'vendors/components/vendorContact/VendorContactCards'
import EditVendorGeneralContactDialog from 'vendors/components/vendorContact/dialogs/EditVendorGeneralContactDialog'
import DeleteUserContactDialog from 'vendors/components/vendorContact/dialogs/deleteUserContactDialog/DeleteUserContactDialog'
import DeleteGeneralContactDialog from 'vendors/components/vendorContact/dialogs/DeleteGeneralContactDialog'
import EditVendorUserContactDialog from 'vendors/components/vendorContact/dialogs/EditVendorUserContactDialog'
import { useTranslation } from 'react-i18next'
import { useGetVendorContactsById } from 'vendors/queries/useVendorQuery'

type DeleteContactType = {
  contactId: string
  contactEmail: string
}

const useVendorId = () => {
  const { vendorId } = useParams<{ vendorId: string }>()
  return vendorId as string 
}

const VendorManageContacts: React.FC = () => {
  const { t } = useTranslation()
  const [editVendorUserContact, setIsEditVendorUserContact] = React.useState<string>('')
  const [editVendorGeneralContact, setIsEditVendorGeneralContact] = React.useState<string>('')
  const [isOpen, setIsOpen] = React.useState(false)

  const [deleteUserContact, setDeleteUserContact] = React.useState<DeleteContactType | null>(null)
  const [deleteGeneralContact, setDeleteGeneralContact] = React.useState<DeleteContactType | null>(null)

  const vendorId = useVendorId() 
  
  const { data: contacts, isLoading: isContactsLoading } = useGetVendorContactsById(vendorId || '')
  
  if (!vendorId) {
    return <Navigate to="/vendors" replace />
  }
  
  const handleOpenAddVendorContactDialog = () => {
    setIsOpen(true)
  }

  const handleOpenEditVendorContactDialog = (userContactId: string) => {
    setIsEditVendorUserContact(userContactId)
  }

  const handleOpenEditGeneralContactDialog = (generalContactId: string) => {
    setIsEditVendorGeneralContact(generalContactId)
  }

  const handleOpenDeleteUserContactDialog = (contactId: string, contactEmail: string) => {
    setDeleteUserContact({ contactId, contactEmail })
  }

  const handleOpenDeleteGeneralContactDialog = (contactId: string, contactEmail: string) => {
    setDeleteGeneralContact({ contactId, contactEmail })
  }

  return (
    <>
      <VendorContactHeader />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, px: 2 }}>
        <Box sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            color="grey"
            onClick={handleOpenAddVendorContactDialog}
            disabled={isContactsLoading}
          >
            {t('add')}
          </Button>
        </Box>
      </Box>

      <VendorContactCards
        contacts={contacts}
        isContactsLoading={isContactsLoading}
        vendorId={vendorId} 
        onVendorContactEdit={handleOpenEditVendorContactDialog}
        onVendorGeneralContactEdit={handleOpenEditGeneralContactDialog}
        handleOpenDeleteUserContactDialog={handleOpenDeleteUserContactDialog}
        handleOpenDeleteGeneralContactDialog={handleOpenDeleteGeneralContactDialog}
      />
      {isOpen && (
        <AddVendorContactDialog
          contacts={contacts}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          vendorId={vendorId} 
        />
      )}
      {editVendorUserContact && (
        <EditVendorUserContactDialog
          onClose={() => setIsEditVendorUserContact('')}
          vendorId={vendorId} 
          userContactId={editVendorUserContact}
        />
      )}
      {editVendorGeneralContact && (
        <EditVendorGeneralContactDialog
          onClose={() => setIsEditVendorGeneralContact('')}
          vendorId={vendorId} 
          generalContactId={editVendorGeneralContact}
        />
      )}
      {deleteUserContact && (
        <DeleteUserContactDialog
          isOpen={deleteUserContact !== null}
          onClose={() => setDeleteUserContact(null)}
          vendorId={vendorId} 
          {...deleteUserContact}
        />
      )}
      {deleteGeneralContact && (
        <DeleteGeneralContactDialog
          isOpen={deleteGeneralContact !== null}
          onClose={() => setDeleteGeneralContact(null)}
          vendorId={vendorId} 
          {...deleteGeneralContact}
        />
      )}
    </>
  )
}

export default VendorManageContacts
